/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
} from '@chakra-ui/react';
import { Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { ICompany } from '../../../../../hooks/useCompanies';
import {
  ITaxAnalysisCompany,
  formatarEmReais,
  formatarPercentual,
  TAX_COLORS,
} from '../../../../../types/taxAnalysis';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';

interface SortableThProps {
  field: string;
  label: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  // eslint-disable-next-line no-unused-vars
  onSort: (field: string) => void;
  isNumeric?: boolean;
}

function SortableTh({ field, label, orderBy, orderDirection, onSort, isNumeric }: SortableThProps) {
  const isActive = orderBy === field;
  return (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      isNumeric={isNumeric}
      _hover={{ bg: 'gray.100' }}
      userSelect="none"
    >
      <Flex align="center" justify={isNumeric ? 'flex-end' : 'flex-start'} gap={1}>
        <Text as="span">{label}</Text>
        {isActive &&
          (orderDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
      </Flex>
    </Th>
  );
}

interface TaxTableProps {
  companies: ICompany[];
  companiesWithTax: ITaxAnalysisCompany[];
  isLoading: boolean;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  // eslint-disable-next-line no-unused-vars
  onRowClick: (company: ICompany) => void;
  // eslint-disable-next-line no-unused-vars
  onSort: (field: string) => void;
}

export function TaxTable({
  companies,
  companiesWithTax,
  isLoading,
  orderBy,
  orderDirection,
  onRowClick,
  onSort,
}: TaxTableProps) {
  if (isLoading) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  if (!companiesWithTax || companiesWithTax.length === 0) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Text color="gray.500">Nenhuma empresa encontrada</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <SortableTh
                field="id"
                label="ID"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
              />
              <SortableTh
                field="nome"
                label="Empresa"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
              />
              <SortableTh
                field="faturamento"
                label="Faturamento"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="pis"
                label="PIS"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="cofins"
                label="COFINS"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="csll"
                label="CSLL"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="irpj"
                label="IRPJ"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="icms"
                label="ICMS"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="iss"
                label="ISS"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="impostos"
                label="Total Impostos"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <Th isNumeric>% Total</Th>
              <Th textAlign="center">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {companiesWithTax.map(item => {
              const company = companies.find(c => c.id === item.id);
              return (
                <Tr
                  key={item.external_id}
                  _hover={{ bg: 'gray.50' }}
                  cursor="pointer"
                  onClick={() => company && onRowClick(company)}
                >
                  <Td>{item.external_id}</Td>
                  <Td>
                    <Box>
                      <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                        {item.razao_social?.length > 30
                          ? `${item.razao_social.substring(0, 30)}...`
                          : item.razao_social}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatCNPJ(item.cnpj)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Text fontSize="sm">{formatarEmReais(item.total_faturamento)}</Text>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.pis)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.PIS}>
                        {formatarPercentual(item.impostos.pis_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.cofins)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.COFINS}>
                        {formatarPercentual(item.impostos.cofins_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.csll)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.CSLL}>
                        {formatarPercentual(item.impostos.csll_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.irpj)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.IRPJ}>
                        {formatarPercentual(item.impostos.irpj_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.icms)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.ICMS}>
                        {formatarPercentual(item.impostos.icms_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="sm">{formatarEmReais(item.impostos.iss)}</Text>
                      <Text fontSize="xs" color={TAX_COLORS.ISS}>
                        {formatarPercentual(item.impostos.iss_percentual)}
                      </Text>
                    </Box>
                  </Td>
                  <Td isNumeric>
                    <Text fontSize="sm" fontWeight="semibold">
                      {formatarEmReais(item.impostos.total_impostos)}
                    </Text>
                  </Td>
                  <Td isNumeric>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color={
                        item.impostos.total_percentual > 30
                          ? 'red.500'
                          : item.impostos.total_percentual > 20
                            ? 'orange.500'
                            : 'green.500'
                      }
                    >
                      {formatarPercentual(item.impostos.total_percentual)}
                    </Text>
                  </Td>
                  <Td textAlign="center">
                    <IconButton
                      aria-label="Ver detalhes"
                      icon={<Eye size={18} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={e => {
                        e.stopPropagation();
                        if (company) onRowClick(company);
                      }}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
